import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { Eye, EyeOff } from "lucide-react";

import { apiGet, apiPost } from "../lib/api-client";

/* ── inline CSS-in-JS for page-level grid background ── */
const pageStyle = {
  minHeight: "100vh",
  background: `
    linear-gradient(rgba(16,24,40,0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(16,24,40,0.06) 1px, transparent 1px),
    #f7f3ec`,
  backgroundSize: "44px 44px, 44px 44px, auto",
};

export default function LoginPage() {
  const { login, register, loginWithTokens } = useAuth();
  const location = useLocation();
  const [mode, setMode] = useState(location.pathname === "/signup" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("athlete");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(false);

  // Handle Google OAuth redirect callback. Kept active so any in-flight
  // Google logins (e.g. browser back-buttons from the OAuth consent screen)
  // still resolve gracefully even though the "Continue with Google" entry
  // point is hidden in the UI for now.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code) {
      window.history.replaceState({}, "", "/login");
      setBusy(true);
      setError("");
      apiPost('/auth/google', { code })
        .then(res => loginWithTokens(res.data))
        .catch(err => setError(err.response?.data?.detail || "Google sign-in failed."))
        .finally(() => setBusy(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register(email, password, name, role);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={pageStyle} data-testid="login-page">
      <main className="min-h-screen flex items-center justify-center p-5 sm:p-7 lg:py-12">

        {/* ── Auth card (centered, full width up to max) ── */}
        <section className="w-full flex justify-center items-center">
          <div
            className="w-full max-w-[560px]"
            style={{
              background: "rgba(255,255,255,0.88)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(231,223,212,0.92)",
              borderRadius: 28,
              boxShadow: "0 30px 80px rgba(16,24,40,0.08)",
            }}
          >
            <div className="p-5 sm:p-[26px]">
            {/* Mini brand header */}
            <div className="flex items-center gap-2.5 mb-5">
              <div
                className="w-[34px] h-[34px] grid place-items-center text-white font-extrabold text-lg"
                style={{
                  borderRadius: 12,
                  background: "linear-gradient(180deg, #ff6d37, #ff5a1f)",
                }}
              >C</div>
              <span className="text-lg font-extrabold tracking-[-0.03em] text-[#101828]">CapyMatch</span>
            </div>

            {/* Welcome copy */}
            <div className="mb-5">
              <h2 className="text-[26px] sm:text-[34px] font-extrabold tracking-[-0.05em] text-[#101828] mb-1.5 sm:mb-2 leading-tight">
                {mode === "login" ? "Welcome back" : "Get started"}
              </h2>
              <p className="text-sm sm:text-base text-[#667085] leading-[1.55]">
                {mode === "login"
                  ? "Sign in to manage your recruiting, review coach conversations, and take your next best action."
                  : "Create your account to start managing your recruiting pipeline."}
              </p>
            </div>

            {/* Tab switcher */}
            <div
              className="grid grid-cols-2 gap-1.5 sm:gap-2 mb-5 sm:mb-6"
              style={{
                padding: 6,
                borderRadius: 14,
                background: "#f7f5f1",
                border: "1px solid #e7dfd4",
              }}
              data-testid="auth-tabs"
            >
              <button
                onClick={() => { setMode("login"); setError(""); }}
                className={`py-2.5 sm:py-3.5 px-3 sm:px-4 text-[15px] sm:text-lg font-bold cursor-pointer border-0 transition-all duration-200 ${
                  mode === "login" ? "text-[#101828]" : "text-[#667085] bg-transparent"
                }`}
                style={{
                  borderRadius: 10,
                  ...(mode === "login"
                    ? { background: "white", boxShadow: "0 6px 18px rgba(16,24,40,0.06)" }
                    : {}),
                }}
                data-testid="tab-login"
              >
                Sign In
              </button>
              <button
                onClick={() => { setMode("register"); setError(""); }}
                className={`py-2.5 sm:py-3.5 px-3 sm:px-4 text-[15px] sm:text-lg font-bold cursor-pointer border-0 transition-all duration-200 ${
                  mode === "register" ? "text-[#101828]" : "text-[#667085] bg-transparent"
                }`}
                style={{
                  borderRadius: 10,
                  ...(mode === "register"
                    ? { background: "white", boxShadow: "0 6px 18px rgba(16,24,40,0.06)" }
                    : {}),
                }}
                data-testid="tab-register"
              >
                Create Account
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {mode === "register" && (
                <>
                  {/* Name field */}
                  <div className="mb-3 sm:mb-4">
                    <label className="block text-[11px] sm:text-[13px] font-extrabold tracking-[0.08em] text-[#667085] mb-1.5 sm:mb-2">NAME</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      placeholder="Your full name"
                      className="w-full text-[15px] sm:text-lg text-[#101828] outline-none"
                      style={{
                        border: "1px solid #e7dfd4",
                        borderRadius: 12,
                        padding: "14px 16px",
                        background: "#fff",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5)",
                      }}
                      data-testid="input-name"
                    />
                  </div>

                  {/* Role is defaulted to athlete */}
                </>
              )}

              {/* Email */}
              <div className="mb-3 sm:mb-4">
                <label className="block text-[11px] sm:text-[13px] font-extrabold tracking-[0.08em] text-[#667085] mb-1.5 sm:mb-2">EMAIL</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@capymatch.com"
                  className="w-full text-[15px] sm:text-lg text-[#101828] outline-none placeholder:text-[#98a2b3]"
                  style={{
                    border: "1px solid #e7dfd4",
                    borderRadius: 12,
                    padding: "14px 16px",
                    background: "#fff",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5)",
                  }}
                  data-testid="input-email"
                />
              </div>

              {/* Password */}
              <div className="mb-3 sm:mb-4">
                <label className="block text-[11px] sm:text-[13px] font-extrabold tracking-[0.08em] text-[#667085] mb-1.5 sm:mb-2">PASSWORD</label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter password"
                    className="w-full text-[15px] sm:text-lg text-[#101828] outline-none pr-12 placeholder:text-[#98a2b3]"
                    style={{
                      border: "1px solid #e7dfd4",
                      borderRadius: 12,
                      padding: "14px 16px",
                      background: "#fff",
                      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5)",
                    }}
                    data-testid="input-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-[14px] top-1/2 -translate-y-1/2 text-[#98a2b3] hover:text-[#667085] transition-colors bg-transparent border-0 cursor-pointer"
                    data-testid="toggle-password"
                  >
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Remember me + Forgot password */}
              {mode === "login" && (
                <div className="flex items-center justify-between mb-3 sm:mb-4 text-[13px] sm:text-sm text-[#667085]">
                  <label
                    className="flex items-center gap-2.5 cursor-pointer !text-sm !font-normal !tracking-normal !text-[#667085] !mb-0"
                    data-testid="remember-me"
                  >
                    <span
                      className="w-[18px] h-[18px] border border-[#e7dfd4] bg-white flex-shrink-0 cursor-pointer flex items-center justify-center"
                      style={{ borderRadius: 6 }}
                      onClick={(e) => { e.preventDefault(); setRemember(!remember); }}
                    >
                      {remember && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#ff5a1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </span>
                    <span onClick={() => setRemember(!remember)} className="cursor-pointer">Remember me</span>
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-[#475467] no-underline hover:text-[#ff5a1f] transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              )}

              {/* Error */}
              {error && (
                <div
                  className="mb-4 text-[15px] font-semibold"
                  style={{
                    padding: "14px 16px",
                    borderRadius: 14,
                    background: "#fff0ee",
                    border: "1px solid #ffd4cf",
                    color: "#d92d20",
                  }}
                  data-testid="auth-error"
                >
                  {error}
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={busy}
                className="w-full border-0 text-white text-lg sm:text-xl font-extrabold tracking-[-0.02em] cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 transition-all duration-200 hover:brightness-110 active:scale-[0.98] mb-4 sm:mb-5"
                style={{
                  background: "#ff5a1f",
                  borderRadius: 14,
                  padding: "14px 20px",
                  boxShadow: "0 20px 34px rgba(255,90,31,0.22)",
                }}
                data-testid="auth-submit-btn"
              >
                {busy ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  mode === "login" ? "Sign In" : "Create Account"
                )}
              </button>
            </form>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="text-center pb-6 text-sm text-[#98a2b3] flex items-center justify-center gap-3">
        <Link to="/privacy" className="hover:text-[#475467] underline underline-offset-2 transition-colors" data-testid="login-privacy-link">
          Privacy Policy
        </Link>
        <span>&middot;</span>
        <Link to="/terms" className="hover:text-[#475467] underline underline-offset-2 transition-colors" data-testid="login-terms-link">
          Terms of Service
        </Link>
      </footer>
    </div>
  );
}
